import { Helmet } from "react-helmet-async";

interface LandingSEOProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
}

export function LandingSEO({
  title = "AiRTalent",
  description = "South Africa's premier tech recruitment agency specializing in IT talent acquisition. Connect with top software developers, data scientists, and tech professionals across Cape Town, Johannesburg, and Pretoria through our innovative employee referral platform.",
  canonicalUrl = "https://airtap.co.za/",
  ogImage = "https://airtap.co.za/og-image.jpg"
}: LandingSEOProps) {
  const fullTitle = title === "AiRTalent" ? title : `${title} | AiRTalent`;
  
  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content="AiRTalent - South Africa's Leading Tech Recruitment & Employee Referral Platform" />
      <meta name="description" content={description} />
      <meta name="keywords" content="tech recruitment south africa, IT recruitment agency cape town, johannesburg tech jobs, software developer jobs south africa, recruitment agency pretoria, tech talent acquisition SA, employee referral platform, IT headhunting south africa, python developer jobs cape town, react developer johannesburg, DevOps jobs south africa, data scientist recruitment SA, fullstack developer cape town, tech startup jobs johannesburg, remote tech jobs south africa, IT contractor recruitment, tech recruitment consultancy, digital recruitment agency south africa, fintech recruitment cape town, blockchain developer jobs SA" />
      <meta name="author" content="AiRTalent" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="geo.region" content="ZA" />
      <meta name="geo.country" content="South Africa" />
      <meta name="geo.placename" content="Cape Town, Johannesburg, Pretoria" />
      <meta name="ICBM" content="-33.9249, 18.4241" />
      <meta name="geo.position" content="-33.9249;18.4241" />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content="AiRTalent - South Africa's Leading Tech Recruitment Agency" />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="AiRTalent - South Africa's Premier Tech Recruitment Platform" />
      <meta property="og:site_name" content="AiRTalent" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:country-name" content="South Africa" />

      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={canonicalUrl} />
      <meta property="twitter:title" content="AiRTalent - South Africa's Leading Tech Recruitment Agency" />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={ogImage} />
      <meta property="twitter:image:alt" content="AiRTalent - Employee Referral Platform" />

      {/* Additional Meta Tags */}
      <meta name="theme-color" content="#FF6B35" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="AiRTalent" />
      <meta name="format-detection" content="telephone=no" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Structured Data - Local Business */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "AiRTalent",
          "alternateName": "AiRTap",
          "url": "https://airtap.co.za",
          "logo": "https://airtap.co.za/logo.png",
          "description": "South Africa's premier tech recruitment agency specializing in IT talent acquisition and employee referral services",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "ZA",
            "addressRegion": "Western Cape",
            "addressLocality": "Cape Town"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": -33.9249,
            "longitude": 18.4241
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "Cape Town",
              "containedInPlace": {
                "@type": "Country",
                "name": "South Africa"
              }
            },
            {
              "@type": "City", 
              "name": "Johannesburg",
              "containedInPlace": {
                "@type": "Country",
                "name": "South Africa"
              }
            },
            {
              "@type": "City",
              "name": "Pretoria", 
              "containedInPlace": {
                "@type": "Country",
                "name": "South Africa"
              }
            }
          ],
          "serviceType": "Tech Recruitment and IT Talent Acquisition",
          "priceRange": "$$",
          "sameAs": [
            "https://linkedin.com/company/ai-rec/"
          ]
        })}
      </script>

      {/* Structured Data - WebSite */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "AiRTalent",
          "url": "https://airtap.co.za",
          "description": "South Africa's leading tech recruitment platform that transforms hiring through employee referrals and professional networks",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://airtap.co.za/jobs?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        })}
      </script>

      {/* Structured Data - Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Employee Referral Platform",
          "provider": {
            "@type": "Organization",
            "name": "AiRTalent"
          },
          "description": "Connect South African tech companies with top IT talent through employee referrals and professional networks",
          "serviceType": "Tech Recruitment and IT Talent Acquisition",
          "areaServed": {
            "@type": "Country",
            "name": "South Africa"
          }
        })}
      </script>
    </Helmet>
  );
}
